/**
 * HeaderAd Component
 * Slim horizontal ad strip designed for page headers / below navigation
 */

import React, { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useAdTracking } from "@/hooks/useAdTracking";
import type { Advertisement } from "@/types/advertising";

interface HeaderAdProps {
  ad: Advertisement;
  className?: string;
}

const HeaderAd: React.FC<HeaderAdProps> = ({ ad, className = "" }) => {
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  const { adRef, handleClick, impressionTracked } = useAdTracking({
    adId: ad.id,
    placement: ad.placement,
    targetUrl: ad.targetUrl,
  });

  if (!ad.isActive || dismissed) return null;

  const renderContent = () => {
    if (ad.format === "image" && ad.imageUrl && !imageError) {
      return (
        <div className="relative w-full">
          {isLoading && (
            <div className="absolute inset-0 bg-gray-200 animate-pulse" />
          )}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={ad.imageUrl}
            alt={ad.altText || ad.title}
            className="w-full h-auto block"
            onError={() => {
              setImageError(true);
              setIsLoading(false);
            }}
            onLoad={() => setIsLoading(false)}
          />
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center gap-3 px-4 py-1">
        <span className="text-sm font-semibold text-gray-900">{ad.title}</span>
        {ad.description && (
          <span className="hidden md:inline text-sm text-gray-600">
            — {ad.description}
          </span>
        )}
        <span className="text-xs font-medium text-blue-600 hover:text-blue-700 whitespace-nowrap">
          Learn More →
        </span>
      </div>
    );
  };

  return (
    <div
      ref={adRef}
      className={`header-ad relative w-full bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100 ${className}`}
      data-ad-id={ad.id}
      data-tracked={impressionTracked}
      role="complementary"
      aria-label="Header Advertisement"
    >
      <div className="max-w-[1280px] mx-auto relative">
        <div className="flex items-center">
          <span className="text-[10px] text-gray-400 px-2 py-0.5 shrink-0">
            Ad
          </span>
          <Link
            href={ad.targetUrl}
            target="_blank"
            rel="noopener noreferrer sponsored"
            onClick={(e) => handleClick(e)}
            className="flex-1 block overflow-hidden"
            aria-label={`Advertisement: ${ad.title}`}
          >
            {renderContent()}
          </Link>
          <button
            onClick={() => setDismissed(true)}
            className="shrink-0 p-2 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Dismiss advertisement"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default HeaderAd;
